// SampleRequestsList — port of sampler/src/views/SampleRequestsList.vue.
// Modes: 'all' | 'ready' | 'trash'. isFactory + basePath are supplied by the
// host shell (admin = customer, supply = factory) so the same screen serves both.

import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  Plus,
  Trash2,
  RotateCcw,
  Copy,
  ChevronRight,
  AlertTriangle,
  MessageSquare,
  Image as ImageIcon,
  Layers,
  Download,
  ChevronDown,
  PackageCheck,
  BookPlus,
} from 'lucide-react'
import { Card } from '../../components/ui/card'
import {
  PHASES,
  STATUS_STYLES,
  formatSampleCode,
  normalizePhase,
  fmtTime,
  type Sample,
} from './lib'
import {
  listSamples,
  listTrash,
  deleteSample,
  duplicateSample,
  restoreSample,
  purgeSample,
  setPriority,
  exportSamples,
  bulkMarkReceived,
} from './api'
import { toast } from '../../lib/toast'
import { PrioritySelector } from './PrioritySelector'
import { NewRequestModal } from './NewRequestModal'
import { ShareButton } from './ShareButton'
import { useT } from '../../i18n'
import { sampleRequestsListI18n } from '../../i18n/samples/requests-list'
import { samplesCommonI18n } from '../../i18n/samples/common'
import { DataTableShell } from '../../components/ui/data-table-shell'

type Mode = 'all' | 'ready' | 'trash'
type BulkAction = 'delete' | 'restore' | 'purge' | 'received'
type CatalogFilter = '' | 'adopted' | 'not_adopted'

// Mirrors BulkMarkReceivedPayload.ids max_length on the endpoint.
const BULK_RECEIVE_LIMIT = 500

type SortKey = 'code' | 'name' | 'priority' | 'activity' | 'status' | 'eta' | 'updated'

const PHASE_RANK: Record<string, number> = {
  draft: 0,
  ready_for_factory: 1,
  confirmed: 2,
  sample_ready: 3,
  shipped: 4,
  received: 5,
}
const PRIORITY_RANK: Record<string, number> = { urgent: 0, normal: 1, low: 2 }
const DEFAULT_DIR: Record<SortKey, 'asc' | 'desc'> = {
  code: 'desc',
  name: 'asc',
  priority: 'asc',
  activity: 'desc',
  status: 'asc',
  eta: 'asc',
  updated: 'desc',
}

function compareBy(a: Sample, b: Sample, key: SortKey): number {
  switch (key) {
    case 'code':
      return (a.pipeline_number || 0) - (b.pipeline_number || 0)
    case 'name':
      return (a.title || '').localeCompare(b.title || '')
    case 'priority':
      return (PRIORITY_RANK[a.priority || 'normal'] ?? 1) - (PRIORITY_RANK[b.priority || 'normal'] ?? 1)
    case 'activity':
      return (a.last_comment_at || a.updated_at || 0) - (b.last_comment_at || b.updated_at || 0)
    case 'status':
      return (PHASE_RANK[normalizePhase(a.status)] ?? 99) - (PHASE_RANK[normalizePhase(b.status)] ?? 99)
    case 'eta': {
      const av = a.expected_completion_at || 0
      const bv = b.expected_completion_at || 0
      if (!av && !bv) return 0
      if (!av) return 1
      if (!bv) return -1
      return av - bv
    }
    case 'updated':
      return (a.updated_at || 0) - (b.updated_at || 0)
  }
}

function productionInfo(r: Sample) {
  const eta = r.expected_completion_at
  if (!eta) return { has: false, overdue: false, daysOverdue: 0, eta: 0 }
  const phase = normalizePhase(r.status)
  const stillProducing = phase === 'confirmed' || phase === 'sample_ready'
  const nowSec = Date.now() / 1000
  const overdue = stillProducing && eta < nowSec
  return {
    has: true,
    overdue,
    daysOverdue: overdue ? Math.floor((nowSec - eta) / 86400) : 0,
    eta,
  }
}

// Module-level (react-hooks/static-components): defining these inside the list
// component recreated their type every render, remounting the header cells.
function SortArrow({
  k,
  sortKey,
  sortDir,
}: {
  k: SortKey
  sortKey: SortKey
  sortDir: 'asc' | 'desc'
}) {
  return sortKey === k ? (
    <span className="text-[var(--color-info)]">{sortDir === 'asc' ? '▲' : '▼'}</span>
  ) : null
}

function Th({
  k,
  label,
  className,
  sortKey,
  sortDir,
  onSort,
}: {
  k: SortKey
  label: string
  className?: string
  sortKey: SortKey
  sortDir: 'asc' | 'desc'
  onSort: (key: SortKey) => void
}) {
  return (
    <th className={`border-l border-[var(--color-border)] ${className || ''}`}>
      <button
        type="button"
        className="inline-flex items-center gap-1 font-semibold hover:text-[var(--color-text)]"
        onClick={() => onSort(k)}
      >
        {label}
        <SortArrow k={k} sortKey={sortKey} sortDir={sortDir} />
      </button>
    </th>
  )
}

export function SampleRequestsList({
  mode = 'all',
  isFactory = false,
  basePath,
}: {
  mode?: Mode
  isFactory?: boolean
  basePath: string
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const t = useT(sampleRequestsListI18n)
  const sc = useT(samplesCommonI18n)
  const [modalOpen, setModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('updated')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [pendingDelete, setPendingDelete] = useState<Sample | null>(null)
  const [pendingBulk, setPendingBulk] = useState<BulkAction | null>(null)
  const [busy, setBusy] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [producerFilter, setProducerFilter] = useState<string>('')
  const [catalogFilter, setCatalogFilter] = useState<CatalogFilter>('')
  const [hideCompleted, setHideCompleted] = useState(false)

  const isTrash = mode === 'trash'
  const isReady = mode === 'ready'

  const query = useQuery({
    queryKey: ['samples', mode],
    queryFn: () => (isTrash ? listTrash().then((d) => ({ items: d.items, trash_count: 0 })) : listSamples()),
  })
  const items = query.data?.items ?? []
  const trashCount = query.data?.trash_count ?? 0
  // Which of the TICKED rows can actually go to the catalog: arrived, and not
  // adopted yet. Adoption is a decision per sample (Volkan, 2026-09-24), so the
  // worksheet opens with the selection rather than with the whole shelf.
  const adoptableSelected = items.filter(
    (s) =>
      selected.has(s.id) &&
      normalizePhase(s.status) === 'received' &&
      !s.adopted_master_catalog_id,
  )

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['samples'] })
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir(DEFAULT_DIR[key])
    }
  }

  const producerOptions = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of items) if (r.producer_org_id) m.set(r.producer_org_id, r.producer_name || '—')
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [items])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const base = items.filter((r) => {
      const phase = normalizePhase(r.status)
      if (isReady && phase === 'draft') return false
      if (statusFilter && phase !== statusFilter) return false
      if (hideCompleted && (phase === 'shipped' || phase === 'received')) return false
      if (producerFilter && (r.producer_org_id || '') !== producerFilter) return false
      if (catalogFilter === 'adopted' && !r.adopted_master_catalog_id) return false
      if (catalogFilter === 'not_adopted' && r.adopted_master_catalog_id) return false
      if (!q) return true
      const hay = `${r.title || ''} ${r.pipeline_number || ''} ${formatSampleCode(r.pipeline_number)} ${r.producer_name || ''}`.toLowerCase()
      return hay.includes(q)
    })
    return [...base].sort((a, b) => {
      const c = compareBy(a, b, sortKey)
      return sortDir === 'asc' ? c : -c
    })
  }, [
    items,
    search,
    isReady,
    sortKey,
    sortDir,
    statusFilter,
    producerFilter,
    catalogFilter,
    hideCompleted,
  ])

  const stats = useMemo(() => {
    const drafts = filtered.filter((r) => normalizePhase(r.status) === 'draft').length
    const inProd = filtered.filter((r) => normalizePhase(r.status) === 'ready_for_factory').length
    const confirmed = filtered.filter((r) => normalizePhase(r.status) === 'confirmed').length
    if (isTrash)
      return [
        { label: t.stats.inTrash, value: filtered.length },
        { label: t.stats.drafts, value: drafts },
        { label: t.stats.other, value: filtered.length - drafts },
      ]
    if (isReady)
      return [
        { label: t.stats.total, value: filtered.length },
        { label: t.stats.readyForFactory, value: inProd },
        { label: t.stats.inProduction, value: confirmed },
      ]
    return [
      { label: t.stats.drafts, value: drafts },
      { label: t.stats.readyForFactory, value: inProd },
      { label: t.stats.inProduction, value: confirmed },
    ]
  }, [filtered, isTrash, isReady, t])

  const headerTitle = isTrash ? t.titleTrash : isReady ? t.titleReady : t.titleAll
  const headerSub = isTrash
    ? t.subTrash
    : isReady
      ? t.subReady
      : t.subAll

  function open(id: string) {
    if (isTrash) return
    navigate({ to: `${basePath}/${id}` })
  }

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id))
  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(filtered.map((r) => r.id)))
  }

  async function performBulk() {
    if (!pendingBulk) return
    setBusy(true)
    try {
      const ids = [...selected]
      if (pendingBulk === 'received') {
        let marked = 0
        const skipped: Array<{ id: string; reason: string }> = []
        // The endpoint caps a call at BULK_RECEIVE_LIMIT ids; a larger selection
        // is sent in successive calls so it reports partial progress instead of
        // failing whole on validation.
        for (let i = 0; i < ids.length; i += BULK_RECEIVE_LIMIT) {
          const res = await bulkMarkReceived(ids.slice(i, i + BULK_RECEIVE_LIMIT))
          marked += res.marked.length
          skipped.push(...res.skipped)
        }
        reportBulkReceived(marked, skipped)
      } else {
        const fn = pendingBulk === 'delete' ? deleteSample : pendingBulk === 'restore' ? restoreSample : purgeSample
        await Promise.all(ids.map((id) => fn(id)))
      }
      setSelected(new Set())
      setPendingBulk(null)
      invalidate()
    } catch (err) {
      toast.error(t.bulkFailed, {
        description: err instanceof Error ? err.message : t.tryAgain,
      })
      setPendingBulk(null)
      invalidate()
    } finally {
      setBusy(false)
    }
  }

  function reportBulkReceived(marked: number, skipped: Array<{ id: string; reason: string }>) {
    const detail = skipped.length
      ? t.skippedDetail(skipped.length, [...new Set(skipped.map((s) => (t.skipReasons as Record<string, string>)[s.reason] || s.reason))].join(', '))
      : undefined
    if (marked === 0) toast.warning(t.nothingReceived, { description: detail })
    else if (skipped.length) toast.warning(t.markedReceived(marked), { description: detail })
    else toast.success(t.markedReceived(marked))
  }

  async function performRemove() {
    if (!pendingDelete) return
    setBusy(true)
    try {
      await (isTrash ? purgeSample(pendingDelete.id) : deleteSample(pendingDelete.id))
      setPendingDelete(null)
      invalidate()
    } finally {
      setBusy(false)
    }
  }

  async function runExport(format: 'pdf' | 'xls') {
    setExportOpen(false)
    if (selected.size === 0) return
    setExporting(true)
    try {
      await exportSamples([...selected], format)
    } finally {
      setExporting(false)
    }
  }

  async function changePriority(r: Sample, value: string) {
    await setPriority(r.id, value)
    invalidate()
  }

  async function duplicate(r: Sample) {
    const dup = await duplicateSample(r.id)
    if (dup?.id) navigate({ to: `${basePath}/${dup.id}` })
  }

  const bulkLabel =
    pendingBulk === 'delete'
      ? { title: t.bulk.delete.title, body: t.bulk.delete.body(selected.size), confirm: t.bulk.delete.confirm, danger: false, warn: false }
      : pendingBulk === 'restore'
        ? { title: t.bulk.restore.title, body: t.bulk.restore.body(selected.size), confirm: t.bulk.restore.confirm, danger: false, warn: false }
        : pendingBulk === 'received'
          ? {
              title: t.bulk.received.title,
              body: t.bulk.received.body(selected.size),
              confirm: t.bulk.received.confirm,
              danger: false,
              warn: true,
            }
          : { title: t.bulk.purge.title, body: t.bulk.purge.body(selected.size), confirm: t.bulk.purge.confirm, danger: true, warn: false }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[var(--color-text)]">{headerTitle}</h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{headerSub}</p>
        </div>
        {!isReady && !isTrash && (
          <div className="flex items-center gap-2">
            {!isFactory && (
              <button
                onClick={() => navigate({ to: `${basePath}/trash` })}
                className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded inline-flex items-center gap-1 hover:bg-[var(--color-surface-muted)]"
                title={t.trashTitle(trashCount)}
              >
                <Trash2 size={14} aria-hidden="true" />
                {t.trash}
                {trashCount > 0 && (
                  <span className="ml-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">
                    {trashCount}
                  </span>
                )}
              </button>
            )}
            {!isFactory && (
              <button
                onClick={() => setModalOpen(true)}
                className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded inline-flex items-center gap-1"
              >
                <Plus size={14} aria-hidden="true" />
                {t.newRequest}
              </button>
            )}
          </div>
        )}
        {isTrash && (
          <button
            onClick={() => navigate({ to: basePath })}
            className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded inline-flex items-center gap-1 hover:bg-[var(--color-surface-muted)]"
          >
            {t.backToSamples}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {stats.map((s) => (
          <Card key={s.label} className="p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">{s.label}</div>
            <div className="mt-1 text-2xl font-semibold text-[var(--color-text)]">{s.value}</div>
          </Card>
        ))}
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-2.5 shadow-sm">
          <button className="text-xs text-[var(--color-info)] underline" onClick={() => setSelected(new Set())}>
            {t.clear}
          </button>
          <span className="text-sm font-semibold text-[var(--color-text)]">{t.selected(selected.size)}</span>
          <div className="ml-auto flex items-center gap-2">
            {!isTrash && (
              <div className="relative">
                <button
                  className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-info)] rounded inline-flex items-center gap-1 disabled:opacity-50"
                  disabled={exporting}
                  onClick={() => setExportOpen((v) => !v)}
                >
                  <Download size={14} /> {exporting ? t.exporting : t.exportAs} <ChevronDown size={12} />
                </button>
                {exportOpen && (
                  <div className="absolute right-0 z-30 mt-1 w-36 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
                    <button className="block w-full px-3 py-2 text-left text-xs hover:bg-[var(--color-surface-muted)]" onClick={() => runExport('pdf')}>PDF</button>
                    <button className="block w-full px-3 py-2 text-left text-xs hover:bg-[var(--color-surface-muted)]" onClick={() => runExport('xls')}>{t.excel}</button>
                  </div>
                )}
              </div>
            )}
            {!isTrash && !isFactory && (
              <>
                <button className="text-xs px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setPendingBulk('received')}>
                  <PackageCheck size={14} /> {t.markAsReceived}
                </button>
                <button
                  className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-success)] rounded inline-flex items-center gap-1 disabled:opacity-50"
                  disabled={busy || adoptableSelected.length === 0}
                  title={
                    adoptableSelected.length
                      ? t.adoptableTitle(adoptableSelected.length)
                      : t.adoptableNone
                  }
                  onClick={() =>
                    navigate({
                      to: `${basePath}/bulk-adopt`,
                      search: { ids: adoptableSelected.map((s) => s.id).join(',') },
                    })
                  }
                >
                  <BookPlus size={14} /> {t.addToCatalog(adoptableSelected.length)}
                </button>
                <button className="text-xs px-3 py-1.5 bg-rose-600 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setPendingBulk('delete')}>
                  <Trash2 size={14} /> {t.moveToTrash}
                </button>
              </>
            )}
            {isTrash && (
              <>
                <button className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setPendingBulk('restore')}>
                  <RotateCcw size={14} /> {t.restore}
                </button>
                <button className="text-xs px-3 py-1.5 bg-rose-600 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setPendingBulk('purge')}>
                  <Trash2 size={14} /> {t.deleteForever}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Table card */}
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-[var(--color-border)] px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <h2 className="text-sm font-semibold text-[var(--color-text)]">{isTrash ? t.trash : t.allSamples}</h2>
          <div className="flex flex-wrap items-center gap-2">
            {!isTrash && (
              <>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-xs px-2 py-1.5 border border-[var(--color-border)] rounded bg-[var(--color-surface)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">{t.allStatuses}</option>
                  {PHASES.map((p) => (
                    <option key={p.key} value={p.key}>{sc.phases[p.key]}</option>
                  ))}
                </select>
                {producerOptions.length > 0 && (
                  <select
                    value={producerFilter}
                    onChange={(e) => setProducerFilter(e.target.value)}
                    className="text-xs px-2 py-1.5 border border-[var(--color-border)] rounded bg-[var(--color-surface)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t.allProducers}</option>
                    {producerOptions.map(([oid, name]) => (
                      <option key={oid} value={oid}>{name}</option>
                    ))}
                  </select>
                )}
                {!isFactory && (
                  <select
                    value={catalogFilter}
                    onChange={(e) => setCatalogFilter(e.target.value as CatalogFilter)}
                    className="text-xs px-2 py-1.5 border border-[var(--color-border)] rounded bg-[var(--color-surface)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t.catalogAny}</option>
                    <option value="adopted">{t.inCatalog}</option>
                    <option value="not_adopted">{t.notInCatalog}</option>
                  </select>
                )}
                <label className="inline-flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
                  <input type="checkbox" className="h-3.5 w-3.5" checked={hideCompleted} onChange={(e) => setHideCompleted(e.target.checked)} />
                  {t.hideCompleted}
                </label>
              </>
            )}
            <div className="relative min-w-[180px]">
              <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-[var(--color-border)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={t.searchPlaceholder}
              />
            </div>
          </div>
        </div>

        {query.isLoading ? (
          <div className="p-8 text-center text-sm text-[var(--color-text-muted)]">{t.loading}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--color-text-muted)]">{t.empty}</div>
        ) : (
          <DataTableShell variant="embedded">
          <table className="min-w-[1300px] table-fixed">
            <thead>
              <tr>
                <th className="w-10">
                  <input type="checkbox" className="h-4 w-4" checked={allSelected} onChange={toggleAll} aria-label={t.selectAll} />
                </th>
                <Th k="code" label={t.cols.code} className="w-20" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <Th k="name" label={t.cols.name} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <th className="w-28 border-l border-[var(--color-border)]">{t.cols.material}</th>
                <th className="w-28 border-l border-[var(--color-border)]">{t.cols.size}</th>
                <th className="w-28 border-l border-[var(--color-border)]">{t.cols.color}</th>
                <Th k="priority" label={t.cols.priority} className="w-32" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <Th k="activity" label={t.cols.activity} className="w-24" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <Th k="status" label={t.cols.status} className="w-32" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                {!isFactory && (
                  <th className="w-32 border-l border-[var(--color-border)]">{t.cols.catalogue}</th>
                )}
                {!isFactory && (
                  <th className="w-32 border-l border-[var(--color-border)]">{t.cols.producer}</th>
                )}
                <Th k="eta" label={t.cols.productionDate} className="w-32" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <Th k="updated" label={isTrash ? t.cols.deleted : t.cols.lastUpdated} className="w-32" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <th className="w-16 border-l border-[var(--color-border)]" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const prod = productionInfo(r)
                return (
                  <tr
                    key={r.id}
                    tabIndex={isTrash ? -1 : 0}
                    role={isTrash ? undefined : 'button'}
                    className={`${isTrash ? 'cursor-default' : 'cursor-pointer'} ${selected.has(r.id) ? '!bg-[var(--color-surface-muted)]' : ''}`}
                    onClick={() => open(r.id)}
                    onKeyDown={(e) => {
                      if (!isTrash && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        open(r.id)
                      }
                    }}
                  >
                    <td className="py-3 align-middle">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={selected.has(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleRow(r.id)}
                        aria-label={t.selectRow(r.title || t.untitled)}
                      />
                    </td>
                    <td className="border-l border-[var(--color-border)] py-3 align-middle">
                      <span className="font-mono text-xs font-semibold text-[var(--color-text)]">{formatSampleCode(r.pipeline_number)}</span>
                    </td>
                    <td className="border-l border-[var(--color-border)] py-3 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="grid h-[84px] w-[84px] shrink-0 place-items-center overflow-hidden rounded-lg bg-[var(--color-surface-muted)] text-sm font-semibold text-[var(--color-info)]">
                          {r.thumbnail ? (
                            <img src={r.thumbnail} className="h-full w-full object-cover" alt="" loading="lazy" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                          ) : (
                            <span>{(r.title || '?').slice(0, 1).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-[var(--color-text)]">{r.title || t.untitled}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--color-text-muted)]">
                            {!!r.reference_count && (
                              <span className="inline-flex items-center gap-0.5" title={t.references(r.reference_count)}>
                                <Layers size={10} /> {r.reference_count}
                              </span>
                            )}
                            {!!r.photo_count && (
                              <span className="inline-flex items-center gap-0.5" title={t.briefPhotos(r.photo_count)}>
                                <ImageIcon size={10} /> {r.photo_count}
                              </span>
                            )}
                            {!!r.comment_count && (
                              <span className="relative inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-1.5 py-px font-semibold text-blue-700">
                                <MessageSquare size={10} /> {r.comment_count}
                                {!!r.unread_count && (
                                  <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" aria-hidden="true" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {([r.material, r.size, r.color] as const).map((v, ci) => (
                      <td key={ci} className="border-l border-[var(--color-border)] py-3 align-middle text-xs text-[var(--color-text)]">
                        {v ? <span className="line-clamp-2">{v}</span> : <span className="text-[var(--color-text-muted)]">—</span>}
                      </td>
                    ))}
                    <td className="border-l border-[var(--color-border)] py-3 align-middle">
                      <PrioritySelector value={r.priority || 'normal'} editable={!isFactory} size="sm" onChange={(v) => changePriority(r, v)} />
                    </td>
                    <td className="border-l border-[var(--color-border)] py-3 align-middle text-xs text-[var(--color-text-muted)]">
                      {sc.relTime(r.last_comment_at || r.updated_at)}
                    </td>
                    <td className="border-l border-[var(--color-border)] py-3 align-middle">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[normalizePhase(r.status)]}`}>
                        {sc.phaseLabel(r.status)}
                      </span>
                    </td>
                    {!isFactory && (
                      <td className="border-l border-[var(--color-border)] py-3 align-middle text-xs">
                        {r.adopted_master_catalog_id ? (
                          <a
                            href={`/admin/catalog/products/${r.adopted_master_catalog_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-[var(--color-success)] hover:bg-emerald-100"
                          >
                            <BookPlus size={11} /> {t.inCatalogue}
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 font-medium text-[var(--color-text-muted)]">
                            {t.notInCatalogue}
                          </span>
                        )}
                      </td>
                    )}
                    {!isFactory && (
                      <td className="border-l border-[var(--color-border)] py-3 align-middle text-xs">
                        {r.producer_name ? (
                          <span className="inline-flex items-center gap-1 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 font-medium text-[var(--color-text)]">{r.producer_name}</span>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">{t.unassigned}</span>
                        )}
                      </td>
                    )}
                    <td className="border-l border-[var(--color-border)] py-3 align-middle">
                      {prod.has ? (
                        <div className={`flex items-center gap-1 text-xs font-medium tabular-nums ${prod.overdue ? 'text-[var(--color-danger)]' : 'text-[var(--color-text)]'}`}>
                          {prod.overdue && <AlertTriangle size={12} className="shrink-0" />}
                          <div>
                            {sc.fmtDate(prod.eta)}
                            {prod.overdue && <div className="text-[11px] font-semibold text-[var(--color-danger)]">{t.daysOverdue(prod.daysOverdue)}</div>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                    <td className="border-l border-[var(--color-border)] py-3 align-middle">
                      <div className="text-xs font-medium text-[var(--color-text)] tabular-nums">{sc.fmtDate(r.updated_at)}</div>
                      <div className="text-[11px] text-[var(--color-text-muted)] tabular-nums">{fmtTime(r.updated_at)}</div>
                    </td>
                    <td className="border-l border-[var(--color-border)] py-3 align-middle">
                      <div className="flex items-center justify-end gap-0.5">
                        {!isTrash ? (
                          <>
                            <ShareButton id={r.id} label={`${r.title || t.sample} (${formatSampleCode(r.pipeline_number)})`} variant="icon" />
                            {!isFactory && (
                              <>
                                <button className="grid h-9 w-9 place-items-center rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-indigo-700" title={t.duplicate} onClick={(e) => { e.stopPropagation(); void duplicate(r) }}>
                                  <Copy size={16} />
                                </button>
                                <button className="grid h-9 w-9 place-items-center rounded-lg text-[var(--color-text-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]" title={t.moveToTrash} onClick={(e) => { e.stopPropagation(); setPendingDelete(r) }}>
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                            <ChevronRight size={16} className="text-[var(--color-text-muted)]" />
                          </>
                        ) : (
                          <>
                            <button className="grid h-9 w-9 place-items-center rounded-lg text-[var(--color-text-muted)] hover:bg-emerald-50 hover:text-emerald-600" title={t.restore} onClick={(e) => { e.stopPropagation(); void restoreSample(r.id).then(invalidate) }}>
                              <RotateCcw size={16} />
                            </button>
                            <button className="grid h-9 w-9 place-items-center rounded-lg text-[var(--color-text-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]" title={t.deleteForever} onClick={(e) => { e.stopPropagation(); setPendingDelete(r) }}>
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </Card>

      <NewRequestModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={(id) => { setModalOpen(false); navigate({ to: `${basePath}/${id}` }) }} />

      {/* Single delete confirm */}
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <Card className="w-full max-w-sm p-5 space-y-3">
            <h3 className="text-base font-semibold text-[var(--color-text)]">{isTrash ? t.permanentlyDelete : t.moveToTrashQ}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">
              {isTrash
                ? t.deleteForeverBody(pendingDelete.title || t.untitled)
                : t.moveToTrashBody(pendingDelete.title || t.untitled)}
            </p>
            <div className="flex justify-end gap-2">
              <button className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded" disabled={busy} onClick={() => setPendingDelete(null)}>{t.cancel}</button>
              <button className="text-xs px-3 py-1.5 bg-rose-600 text-white rounded disabled:opacity-50" disabled={busy} onClick={performRemove}>
                {isTrash ? t.deleteForever : t.moveToTrash}
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Bulk confirm */}
      {pendingBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <Card className="w-full max-w-sm p-5 space-y-3">
            <h3 className="text-base font-semibold text-[var(--color-text)]">{bulkLabel.title}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{bulkLabel.body}</p>
            <div className="flex justify-end gap-2">
              <button className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded" disabled={busy} onClick={() => setPendingBulk(null)}>{t.cancel}</button>
              <button className={`text-xs px-3 py-1.5 text-white rounded disabled:opacity-50 ${bulkLabel.danger ? 'bg-rose-600' : bulkLabel.warn ? 'bg-amber-500' : 'bg-indigo-600'}`} disabled={busy} onClick={performBulk}>
                {bulkLabel.confirm}
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
